import { FC, Fragment, useMemo } from "react";
import Head from "next/head";
import Page from "@layouts/page";
import Auth, {
  forwardAuthenticated,
  forwardNotAuthenticated,
} from "@modules/auth";

import { PageConfigProps } from "./types";
import NajvaPushNotification from "../najva-push-notification";
import YektanetScript from "legacy/elements/YektanetScript";
import Hotjar from "legacy/elements/hotjar";

const isInternationalScriptsDisabled =
  process.env.NEXT_PUBLIC_IS_INTERNATIONAL_SCRIPTS_ACTIVE === "false";

const PageConfig: FC<PageConfigProps> = ({ children, config }) => {
  const redirectHandler = useMemo(
    () =>
      config?.isAuthRequired
        ? forwardNotAuthenticated
        : forwardAuthenticated,
    [config?.isAuthRequired]
  );

  if (!config) return null;

  const {
    meta,
    jsonLd,
    enableYektanet,
    enableHotjar,
    redirectUrl,
    enableNajvaPushNotification,
    head,
  } = config;

  const content = redirectUrl ? (
    <Auth redirectUrl={redirectUrl} redirectHandler={redirectHandler}>
      {children}
    </Auth>
  ) : (
    <Fragment>{children}</Fragment>
  );

  return (
    <Page meta={meta}>
      <Head>
        {head}
        {jsonLd?.map((schema, index) => (
          <script
            key={`json-ld-${index}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(schema),
            }}
          />
        ))}
      </Head>

      {enableYektanet && <YektanetScript />}

      {enableHotjar && !isInternationalScriptsDisabled && <Hotjar />}
      {enableNajvaPushNotification && <NajvaPushNotification />}

      {content}
    </Page>
  );
};

export default PageConfig;
